import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../../environment";

interface ApiScope {
  api_scope_id: number;
  scope_key: string;
  scope_display_name: string;
  description?: string;
  is_active?: boolean;
  crm_entity_id?: number;
}

interface SwaggerField {
  name: string;
  type: string;
  required: boolean;
}

interface SwaggerMethodData {
  fields?: SwaggerField[];
  extra?: any;
  description?: string;
}

interface SwaggerEntity {
  filter_parameters?: SwaggerField[];
  post?: SwaggerMethodData;
  get?: SwaggerMethodData;
  single_get?: SwaggerMethodData;
  put?: SwaggerMethodData;
  patch?: SwaggerMethodData;
  delete?: SwaggerMethodData | { description: string };
  pagination_enabled?: boolean;
  get_list_response_structure?: any;
  get_list_response_example?: any;
}

interface DynamicSection {
  id: string;
  title: string;
  description: string;
  entityKey: string;
  entityData: SwaggerEntity;
}

const formatEntityName = (key: string): string =>
  key
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const buildFieldsJson = (fields: SwaggerField[], extra?: any): string => {
  const obj: Record<string, any> = {};
  fields.forEach((f) => {
    if (f.type === "integer") obj[f.name] = 1;
    else if (f.type === "boolean") obj[f.name] = true;
    else obj[f.name] = "string";
  });
  if (extra && typeof extra === "object") {
    Object.assign(obj, extra);
  }
  return JSON.stringify(obj, null, 2);
};

const ApiDocumentation = () => {
  const [activeTab, setActiveTab] = useState<
    "getting-started" | "endpoints" | "scopes" | "authentication"
  >("getting-started");
  const [selectedSection, setSelectedSection] =
    useState<string>("authentication");
  const [scopes, setScopes] = useState<ApiScope[]>([]);
  const [loading, setLoading] = useState(false);
  const [swaggerLoading, setSwaggerLoading] = useState(false);
  const [dynamicSections, setDynamicSections] = useState<DynamicSection[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [scopeSearchQuery, setScopeSearchQuery] = useState("");
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [collapsedCards, setCollapsedCards] = useState<string[]>([]);

  const toggleCard = (cardId: string) => {
    setCollapsedCards((prev) =>
      prev.includes(cardId) ? prev.filter((c) => c !== cardId) : [...prev, cardId]
    );
  };

  useEffect(() => {
    const authCred = localStorage.getItem("authCred");
    const token = localStorage.getItem("token");
    const loggedIn = !!(authCred || token);
    setIsLoggedIn(loggedIn);

    if (loggedIn) {
      try {
        const authResponse = localStorage.getItem("authResponse");
        if (authResponse) {
          const authData = JSON.parse(authResponse);
          const isApiAdmin = authData?.tenants?.some(
            (t: any) =>
              t.role_key?.toLowerCase() === "apiadmin" ||
              t.role_key?.toLowerCase() === "api_admin" ||
              t.role_name?.toLowerCase() === "apiadmin" ||
              t.role_name?.toLowerCase() === "api_admin"
          );
          if (isApiAdmin) {
            setUserRole("apiadmin");
          } else {
            setUserRole("clientadmin");
          }
        } else {
          setUserRole("clientadmin");
        }
      } catch (_) {
        setUserRole("clientadmin");
      }
    } else {
      setUserRole("clientadmin");
    }
  }, []);

  useEffect(() => {
    fetchScopes();
  }, []);

  useEffect(() => {
    if (userRole) {
      fetchSwaggerData();
    }
  }, [userRole]);

  useEffect(() => {
    if (searchQuery.length > 2) {
      performSearch(searchQuery);
    } else {
      setSearchResults([]);
      setIsSearching(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    setCurrentPage(1);
  }, [scopeSearchQuery]);

  useEffect(() => {
    if (selectedSection && activeTab === "endpoints") {
      if (!expandedSections.includes(selectedSection)) {
        setExpandedSections((prev) => [...prev, selectedSection]);
      }
    }
  }, [selectedSection, activeTab]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchScopes = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api-scope/?skip_limit=true`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (response.ok) {
        const data = await response.json();
        setScopes(data || []);
      }
    } catch (error) {
      console.error("Error fetching API scopes:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSwaggerData = async () => {
    setSwaggerLoading(true);
    try {
      let apiNames: string[] | null = null;

      if (userRole !== "apiadmin") {
        const entityRes = await fetch(
          `${API_BASE_URL}/crm-entity/?is_open_api_enabled=true`,
          { method: "GET", headers: { "Content-Type": "application/json" } }
        );
        if (entityRes.ok) {
          const entityData = await entityRes.json();
          const results = entityData.results || entityData || [];
          apiNames = results.map((e: any) =>
            (e.crm_entity_key || "").toLowerCase().replace(/_/g, "-")
          );
        }
      }

      const payload: any = { assigned_user_role: userRole };
      if (apiNames !== null) payload.api_names = apiNames;

      const swaggerRes = await fetch(`${API_BASE_URL}/open-api-swagger/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (swaggerRes.ok) {
        const swaggerData = await swaggerRes.json();
        const entities: any[] = swaggerData.entities || [];
        const sections: DynamicSection[] = entities.flatMap((entityObj: any) =>
          Object.keys(entityObj).map((key) => ({
            id: key,
            title: formatEntityName(key),
            description: `Manage ${formatEntityName(key)} records via the Cloudee CRM API.`,
            entityKey: key,
            entityData: entityObj[key] as SwaggerEntity,
          }))
        );
        setDynamicSections(sections);
      }
    } catch (error) {
      console.error("Error fetching swagger data:", error);
    } finally {
      setSwaggerLoading(false);
    }
  };

  const performSearch = (query: string) => {
    setIsSearching(true);
    const groupedResults: Record<string, any[]> = {};
    const lowerQuery = query.toLowerCase();

    dynamicSections.forEach((section) => {
      const sectionMatches: any[] = [];

      if (section.title.toLowerCase().includes(lowerQuery)) {
        sectionMatches.push({
          type: "endpoint",
          method: "GET",
          endpoint: `/${section.entityKey}/`,
          description: `List all ${section.title}`,
          sectionId: section.id,
          methodIndex: 0,
          tab: "endpoints",
        });
      }

      if (sectionMatches.length > 0) {
        groupedResults[section.title] = sectionMatches;
      }
    });

    if (
      "authentication".includes(lowerQuery) ||
      "api key".includes(lowerQuery) ||
      "api secret".includes(lowerQuery) ||
      "credentials".includes(lowerQuery)
    ) {
      groupedResults["Getting started"] =
        groupedResults["Getting started"] || [];
      groupedResults["Getting started"].push({
        type: "page",
        title: "Authentication",
        description: "Learn how to authenticate API requests",
        tab: "authentication",
      });
    }

    if (
      "getting started".includes(lowerQuery) ||
      "quick start".includes(lowerQuery)
    ) {
      groupedResults["Getting started"] =
        groupedResults["Getting started"] || [];
      groupedResults["Getting started"].push({
        type: "page",
        title: "Getting Started",
        description: "Quick start guide",
        tab: "getting-started",
      });
    }

    if ("scopes".includes(lowerQuery) || "permissions".includes(lowerQuery)) {
      groupedResults["Getting started"] =
        groupedResults["Getting started"] || [];
      groupedResults["Getting started"].push({
        type: "page",
        title: "API Scopes",
        description: "Manage API access permissions",
        tab: "scopes",
      });
    }

    setSearchResults(Object.entries(groupedResults));
  };

  const methodColors: Record<string, string> = {
    GET: "bg-soft-info text-info",
    POST: "bg-soft-success text-success",
    PATCH: "bg-soft-warning text-warning",
    PUT: "bg-soft-warning text-warning",
    DELETE: "bg-soft-danger text-danger",
  };

  const handleSearchResultClick = (result: any) => {
    setActiveTab(result.tab);
    setSearchQuery("");
    setSearchResults([]);
    setIsSearching(false);

    if (result.sectionId) {
      setSelectedSection(result.sectionId);

      if (!expandedSections.includes(result.sectionId)) {
        setExpandedSections((prev) => [...prev, result.sectionId]);
      }

      if (result.methodIndex !== undefined) {
        setSelectedMethod(`${result.sectionId}-${result.methodIndex}`);
        setTimeout(() => {
          const element = document.getElementById(
            `${result.sectionId}-method-${result.methodIndex}`
          );
          element?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      } else {
        setSelectedMethod(null);
        setTimeout(() => {
          document
            .getElementById(result.sectionId)
            ?.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults([]);
    setIsSearching(false);
  };

  const filteredScopes = scopes.filter(
    (scope) =>
      scope.scope_display_name
        .toLowerCase()
        .includes(scopeSearchQuery.toLowerCase()) ||
      (scope.description &&
        scope.description
          .toLowerCase()
          .includes(scopeSearchQuery.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredScopes.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedScopes = filteredScopes.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    document
      .getElementById("scopes-table-section")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  const handleCopyCode = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(id);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  const CodeBlock = ({ code, id }: { code: string; id: string }) => (
    <div style={{ position: "relative" }}>
      <div className="code-block">
        <pre style={{ margin: 0 }}>{code}</pre>
      </div>
      <button
        className="copy-code-btn"
        onClick={() => handleCopyCode(code, id)}
        title={copiedCode === id ? "Copied!" : "Copy code"}
      >
        {copiedCode === id ? (
          <i className="ti ti-check"></i>
        ) : (
          <i className="ti ti-copy"></i>
        )}
      </button>
    </div>
  );

  const toggleSection = (sectionId: string, navigate: boolean = false) => {
    setExpandedSections((prev) =>
      prev.includes(sectionId)
        ? prev.filter((id) => id !== sectionId)
        : [...prev, sectionId]
    );

    if (navigate) {
      setActiveTab("endpoints");
      setSelectedSection(sectionId);
      setSelectedMethod(null);
      setTimeout(() => {
        document
          .getElementById(sectionId)
          ?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    }
  };

  const handleMethodClick = (sectionId: string, methodIndex: number) => {
    setActiveTab("endpoints");
    setSelectedSection(sectionId);

    if (!expandedSections.includes(sectionId)) {
      setExpandedSections((prev) => [...prev, sectionId]);
    }

    setSelectedMethod(`${sectionId}-${methodIndex}`);
    setTimeout(() => {
      const element = document.getElementById(
        `${sectionId}-method-${methodIndex}`
      );
      element?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!userRole) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fff",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#fff" }}>
      <style>
        {`
          .docs-nav-header {
            background: white;
            border-bottom: 1px solid #e0e0e0;
            padding: 16px 0;
            position: sticky;
            top: 0;
            z-index: 1000;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
          }

          .docs-nav-header .nav-brand {
            font-size: 20px;
            font-weight: 700;
            color: #2d3748;
            text-decoration: none;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .docs-nav-header .nav-link {
            color: #4a5568;
            text-decoration: none;
            padding: 8px 16px;
            font-weight: 500;
            transition: color 0.2s ease;
          }

          .docs-nav-header .nav-link:hover {
            color: #6b2ee7;
          }

          .docs-search-section {
            background: linear-gradient(135deg, #d4f4dd 0%, #b8e6c3 100%);
            padding: 60px 0;
            text-align: center;
          }

          .docs-search-box {
            max-width: 600px;
            margin: 0 auto;
            position: relative;
          }

          .docs-search-box input {
            width: 100%;
            padding: 16px 20px 16px 50px;
            border: 1px solid #cbd5e0;
            border-radius: 8px;
            font-size: 16px;
            background: white;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          }

          .docs-search-box input:focus {
            outline: none;
            border-color: #6b2ee7;
            box-shadow: 0 2px 12px rgba(107, 46, 231, 0.15);
          }

          .docs-search-box .search-icon {
            position: absolute;
            left: 18px;
            top: 50%;
            transform: translateY(-50%);
            color: #718096;
            font-size: 20px;
          }

          .search-results-dropdown {
            position: absolute;
            top: calc(100% + 8px);
            left: 0;
            right: 0;
            background: white;
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
            max-height: 500px;
            overflow-y: auto;
            z-index: 1000;
            border: 1px solid #e0e0e0;
          }

          .search-results-dropdown::-webkit-scrollbar {
            width: 8px;
          }

          .search-results-dropdown::-webkit-scrollbar-track {
            background: #f1f1f1;
          }

          .search-results-dropdown::-webkit-scrollbar-thumb {
            background: #cbd5e0;
            border-radius: 4px;
          }

          .search-group {
            border-bottom: 1px solid #e0e0e0;
          }

          .search-group:last-child {
            border-bottom: none;
          }

          .search-group-header {
            padding: 12px 20px;
            background: #f8f9fa;
            border-bottom: 1px solid #e0e0e0;
            display: flex;
            align-items: center;
            justify-content: space-between;
            position: sticky;
            top: 0;
            z-index: 1;
          }

          .search-group-title {
            font-weight: 600;
            color: #2d3748;
            font-size: 13px;
          }

          .search-group-count {
            background: #e2e8f0;
            color: #4a5568;
            padding: 2px 8px;
            border-radius: 12px;
            font-size: 12px;
            font-weight: 600;
          }

          .search-result-item {
            padding: 12px 20px;
            cursor: pointer;
            transition: background 0.15s ease;
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid #f0f0f0;
          }

          .search-result-item:hover {
            background: #f8f9fa;
          }

          .search-result-item:last-child {
            border-bottom: none;
          }

          .search-result-content {
            flex: 1;
          }

          .search-result-title {
            font-weight: 500;
            color: #2d3748;
            font-size: 14px;
            margin-bottom: 2px;
          }

          .search-result-description {
            color: #718096;
            font-size: 12px;
            line-height: 1.4;
          }

          .search-result-method {
            padding: 4px 10px;
            border-radius: 4px;
            font-size: 11px;
            font-weight: 600;
            margin-left: 12px;
            flex-shrink: 0;
          }

          .no-results {
            padding: 40px 20px;
            text-align: center;
            color: #718096;
            font-size: 14px;
          }

          .clear-search-btn {
            position: absolute;
            right: 18px;
            top: 50%;
            transform: translateY(-50%);
            background: none;
            border: none;
            color: #718096;
            cursor: pointer;
            padding: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 4px;
            transition: all 0.15s ease;
          }

          .clear-search-btn:hover {
            background: #e2e8f0;
            color: #2d3748;
          }

          .mobile-menu-toggle {
            display: none;
            background: none;
            border: none;
            color: #4a5568;
            font-size: 24px;
            cursor: pointer;
            padding: 8px;
            margin-right: 12px;
            order: -1;
          }

          .mobile-menu-dropdown {
            position: absolute;
            top: 60px;
            left: 10px;
            background: white;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            min-width: 250px;
            max-height: calc(100vh - 80px);
            overflow-y: auto;
            z-index: 1100;
            display: none;
          }

          .mobile-menu-dropdown.show {
            display: block;
          }

          .mobile-menu-item {
            padding: 12px 16px;
            cursor: pointer;
            transition: background 0.15s ease;
            display: flex;
            align-items: center;
            gap: 10px;
            color: #2d3748;
            text-decoration: none;
            font-size: 14px;
            font-weight: 500;
            border: none;
          }

          .mobile-menu-item:hover {
            background: #f8f9fa;
          }

          .mobile-menu-item.active {
            background: #f0e6ff;
            color: #6b2ee7;
            font-weight: 600;
          }

          .mobile-menu-item.primary {
            background: #6b2ee7;
            color: white;
            margin: 8px;
            border-radius: 6px;
          }

          .mobile-menu-item.primary:hover {
            background: #5a25c7;
          }

          .mobile-menu-item.outline {
            color: #6b2ee7;
            border: 1px solid #6b2ee7;
            margin: 8px;
            border-radius: 6px;
          }

          .mobile-menu-item.outline:hover {
            background: #f0e6ff;
          }

          .mobile-menu-item .section-expand-icon {
            transition: transform 0.2s ease;
            font-size: 14px;
          }

          .mobile-menu-item .section-expand-icon.expanded {
            transform: rotate(180deg);
          }

          .mobile-menu-divider {
            height: 1px;
            background: #e0e0e0;
            margin: 8px 0;
          }

          .mobile-menu-section-title {
            font-size: 12px;
            font-weight: 600;
            color: #6b2ee7;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            padding: 12px 16px 8px;
            margin-top: 8px;
          }

          .mobile-actions-hidden {
            display: none !important;
          }

          @media (max-width: 767px) {
            .mobile-menu-toggle {
              display: flex;
              align-items: center;
              justify-content: center;
            }

            .nav-brand {
              margin-left: 10vh !important;
            }

            .mobile-hide {
              display: none !important;
            }

            .docs-nav-header .d-flex.align-items-center.gap-4 {
              gap: 8px !important;
            }
          }

          .docs-header {
            background: white;
            padding: 0;
            color: #2d3748;
          }

          .docs-sidebar {
            position: sticky;
            top: 80px;
            max-height: calc(100vh - 100px);
            overflow-y: auto;
            background: #f8f9fa;
            border-right: 1px solid #e0e0e0;
            padding: 30px 20px;
          }

          .docs-sidebar::-webkit-scrollbar {
            width: 4px;
          }

          .docs-sidebar::-webkit-scrollbar-track {
            background: transparent;
          }

          .docs-sidebar::-webkit-scrollbar-thumb {
            background: #cbd5e0;
            border-radius: 4px;
          }

          .sidebar-section-title {
            font-size: 18px;
            font-weight: 700;
            color: #1a202c;
            margin-bottom: 20px;
            padding-bottom: 10px;
            border-bottom: 2px solid #e0e0e0;
          }

          .sidebar-subsection {
            margin-bottom: 24px;
          }

          .sidebar-subsection-title {
            font-size: 14px;
            font-weight: 600;
            color: #6b2ee7;
            margin-bottom: 8px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          .docs-nav-link {
            display: block;
            padding: 8px 12px;
            margin: 2px 0;
            color: #4a5568;
            text-decoration: none;
            border-radius: 4px;
            transition: all 0.15s ease;
            font-weight: 400;
            font-size: 14px;
            cursor: pointer;
          }

          .docs-nav-link:hover {
            background-color: #e2e8f0;
            color: #2d3748;
            padding-left: 16px;
          }

          .docs-nav-link.active {
            background-color: #6b2ee7;
            color: white;
            font-weight: 500;
          }

          .docs-nav-section {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 8px 12px;
            margin: 2px 0;
            color: #4a5568;
            text-decoration: none;
            border-radius: 4px;
            transition: all 0.15s ease;
            font-weight: 500;
            font-size: 14px;
            cursor: pointer;
            background: white;
          }

          .docs-nav-section:hover {
            background-color: #e2e8f0;
            color: #2d3748;
          }

          .docs-nav-section.active {
            background-color: #f0e6ff;
            color: #6b2ee7;
          }

          .section-expand-icon {
            transition: transform 0.2s ease;
            font-size: 12px;
          }

          .section-expand-icon.expanded {
            transform: rotate(180deg);
          }

          .docs-nav-submenu {
            max-height: 0;
            overflow: hidden;
            transition: max-height 0.3s ease;
            padding-left: 12px;
          }

          .docs-nav-submenu.expanded {
            max-height: 1000px;
          }

          .docs-nav-submenu-item {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 12px;
            margin: 2px 0;
            color: #4a5568;
            text-decoration: none;
            border-radius: 4px;
            transition: all 0.15s ease;
            font-weight: 400;
            font-size: 13px;
            cursor: pointer;
            border-left: 2px solid transparent;
          }

          .docs-nav-submenu-item:hover {
            background-color: #e2e8f0;
            border-left-color: #cbd5e0;
          }

          .docs-nav-submenu-item.active {
            background-color: #f0e6ff;
            color: #6b2ee7;
            border-left-color: #6b2ee7;
          }

          .method-badge-small {
            font-size: 10px;
            font-weight: 600;
            padding: 2px 6px;
            border-radius: 3px;
            min-width: 50px;
            text-align: center;
          }

          .docs-content {
            background: white;
            padding: 40px 50px;
            max-width: 1000px;
          }

          .docs-content h2 {
            font-size: 32px;
            font-weight: 700;
            color: #1a202c;
            margin-bottom: 16px;
          }

          .docs-content p {
            font-size: 16px;
            line-height: 1.7;
            color: #4a5568;
          }

          .endpoint-card {
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            margin-bottom: 20px;
            overflow: hidden;
            transition: all 0.2s ease;
            scroll-margin-top: 100px;
          }

          .endpoint-card:hover {
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            border-color: #6b2ee7;
          }

          .endpoint-header {
            padding: 20px;
            background: #f8f9fa;
            border-bottom: 1px solid #e0e0e0;
            cursor: pointer;
          }

          .endpoint-body {
            padding: 20px;
          }

          .code-block {
            background: #2d3748;
            color: #e2e8f0;
            padding: 20px;
            border-radius: 6px;
            overflow-x: auto;
            font-family: 'Courier New', monospace;
            font-size: 14px;
            line-height: 1.6;
          }

          .copy-code-btn {
            position: absolute;
            top: 12px;
            right: 12px;
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.2);
            color: #e2e8f0;
            padding: 8px 12px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 16px;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            backdrop-filter: blur(10px);
          }

          .copy-code-btn:hover {
            background: rgba(255, 255, 255, 0.2);
            border-color: rgba(255, 255, 255, 0.3);
          }

          .copy-code-btn:active {
            transform: scale(0.95);
          }

          .copy-code-btn i {
            font-size: 18px;
          }

          .param-table {
            width: 100%;
            border-collapse: collapse;
            margin: 15px 0;
          }

          .param-table th {
            background: #f8f9fa;
            padding: 12px;
            text-align: left;
            font-weight: 600;
            border-bottom: 2px solid #e0e0e0;
          }

          .param-table td {
            padding: 12px;
            border-bottom: 1px solid #e0e0e0;
          }

          .required-badge {
            background: #dc3545;
            color: white;
            font-size: 10px;
            padding: 2px 6px;
            border-radius: 4px;
            font-weight: 600;
          }

          .optional-badge {
            background: #6c757d;
            color: white;
            font-size: 10px;
            padding: 2px 6px;
            border-radius: 4px;
            font-weight: 600;
          }

          .docs-main-wrapper {
            display: flex;
            min-height: calc(100vh - 70px);
          }

          .docs-sidebar-wrapper {
            width: 280px;
            flex-shrink: 0;
          }

          .docs-content-wrapper {
            flex: 1;
            overflow-x: hidden;
          }

          .search-box {
            padding: 10px 15px;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            width: 100%;
            margin-bottom: 20px;
          }

          .search-box:focus {
            outline: none;
            border-color: #6b2ee7;
            box-shadow: 0 0 0 3px rgba(107, 46, 231, 0.1);
          }

          .scope-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
          }

          .scope-table thead tr {
            background: #f9fafb;
            border-bottom: 1px solid #e5e7eb;
          }

          .scope-table th {
            padding: 12px 16px;
            text-align: left;
            font-weight: 600;
            font-size: 13px;
            color: #6b7280;
            letter-spacing: 0.05em;
            border-bottom: 1px solid #e5e7eb;
          }

          .scope-table tbody tr {
            border-bottom: 1px solid #e5e7eb;
            transition: background-color 0.2s ease;
          }

          .scope-table tbody tr:hover {
            background: #f9fafb;
          }

          .scope-table td {
            padding: 16px;
            vertical-align: middle;
            font-size: 14px;
            color: #1f2937;
          }

          .scope-search-box {
            padding: 10px 15px;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            width: 100%;
            margin-bottom: 20px;
            font-size: 14px;
          }

          .scope-search-box:focus {
            outline: none;
            border-color: #6b2ee7;
            box-shadow: 0 0 0 3px rgba(107, 46, 231, 0.1);
          }

          .pagination-container {
            display: flex;
            justify-content: center;
            margin-top: 30px;
            margin-bottom: 20px;
          }

          .pagination {
            display: flex;
            list-style: none;
            padding: 0;
            margin: 0;
            gap: 8px;
            flex-wrap: wrap;
          }

          .scroll-to-top {
            position: fixed;
            bottom: 30px;
            right: 30px;
            width: 50px;
            height: 50px;
            background: #6b2ee7;
            color: white;
            border: none;
            border-radius: 50%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            box-shadow: 0 4px 12px rgba(107, 46, 231, 0.3);
            transition: all 0.3s ease;
            z-index: 999;
            opacity: 0;
            visibility: hidden;
            transform: translateY(20px);
          }

          .scroll-to-top.show {
            opacity: 1;
            visibility: visible;
            transform: translateY(0);
          }

          .scroll-to-top:hover {
            background: #5a25c7;
            box-shadow: 0 6px 16px rgba(107, 46, 231, 0.4);
            transform: translateY(-3px);
          }

          .scroll-to-top:active {
            transform: translateY(-1px);
          }

          .page-item {
            margin: 0;
          }

          .page-link {
            padding: 8px 16px;
            border: 1px solid #e0e0e0;
            background: white;
            color: #4a5568;
            cursor: pointer;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            transition: all 0.2s ease;
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .page-link:hover:not(:disabled) {
            background: #f8f9fa;
            border-color: #6b2ee7;
            color: #6b2ee7;
          }

          .page-item.active .page-link {
            background: #6b2ee7;
            color: white;
            border-color: #6b2ee7;
          }

          .page-item.disabled .page-link {
            cursor: not-allowed;
            opacity: 0.5;
            background: #f8f9fa;
          }

          .page-link:disabled {
            cursor: not-allowed;
            opacity: 0.5;
          }

          @media (max-width: 767px) {
            .docs-nav-header .container-fluid {
              justify-content: center;
            }

            .docs-nav-header .d-flex.align-items-center.gap-2 {
              width: 100%;
              justify-content: center;
            }

            .mobile-menu-toggle {
              position: absolute;
              left: 10px;
            }

            .mobile-hide {
              display: none !important;
            }

            .nav-brand {
              margin: 0 auto;
            }
          }

          @media (max-width: 991px) {
            .docs-main-wrapper {
              flex-direction: column;
            }

            .docs-sidebar-wrapper {
              display: none;
            }

            .docs-content {
              padding: 30px 20px;
            }
          }
        `}
      </style>

      <div className="docs-nav-header">
        <div className="container-fluid px-4">
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center gap-2">
              <button
                className="mobile-menu-toggle"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                aria-label="Menu"
              >
                <i className="fas fa-ellipsis-h"></i>
              </button>
              <Link to="/" className="nav-brand">
                <img
                  src="/assets/img/Logo.png"
                  alt="Logo"
                  style={{
                    width: "150px",
                    height: "90px",
                    marginTop: "-26px",
                    marginBottom: "-26px",
                  }}
                />
              </Link>
            </div>
            <div className="d-flex align-items-center gap-3">
              {isLoggedIn ? (
                <Link
                  to="/dashboard/crm-dashboard"
                  className="btn btn-md btn-primary mobile-hide"
                >
                  <i className="ti ti-layout-dashboard me-1"></i>
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="btn btn-sm btn-outline-primary d-none d-md-inline-flex"
                  >
                    <i className="ti ti-login me-1 mt-1"></i>
                    Log in
                  </Link>
                  <Link
                    to="/register"
                    className="btn btn-sm btn-primary mobile-hide"
                  >
                    <i className="ti ti-user-plus me-1"></i>
                    Sign up
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {showMobileMenu && (
          <>
            <div
              className={`mobile-menu-dropdown ${showMobileMenu ? "show" : ""}`}
              onClick={(e) => e.stopPropagation()}
            >
              {isLoggedIn ? (
                <Link
                  to="/dashboard/crm-dashboard"
                  className="mobile-menu-item primary"
                  onClick={() => setShowMobileMenu(false)}
                >
                  <i className="ti ti-layout-dashboard"></i>
                  Go to Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="mobile-menu-item outline"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <i className="ti ti-login"></i>
                    Log in
                  </Link>
                  <Link
                    to="/register"
                    className="mobile-menu-item primary"
                    onClick={() => setShowMobileMenu(false)}
                  >
                    <i className="ti ti-user-plus"></i>
                    Sign up
                  </Link>
                </>
              )}

              <div className="mobile-menu-divider"></div>

              <div className="mobile-menu-section-title">Getting Started</div>
              <a
                href="#"
                className={`mobile-menu-item ${activeTab === "getting-started" ? "active" : ""
                  }`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("getting-started");
                  setShowMobileMenu(false);
                }}
              >
                <i className="fa-solid fa-rocket"></i>
                Quick Start
              </a>
              <a
                href="#"
                className={`mobile-menu-item ${activeTab === "authentication" ? "active" : ""
                  }`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("authentication");
                  setShowMobileMenu(false);
                }}
              >
                <i className="ti ti-shield-lock"></i>
                Authentication
              </a>
              <a
                href="#"
                className={`mobile-menu-item ${activeTab === "scopes" ? "active" : ""
                  }`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("scopes");
                  setShowMobileMenu(false);
                }}
              >
                <i className="ti ti-lock-access"></i>
                Scopes
              </a>

              <div className="mobile-menu-section-title">Endpoints</div>
              {swaggerLoading ? (
                <div className="mobile-menu-item" style={{ color: "#718096" }}>
                  <i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }}></i>
                  Loading endpoints...
                </div>
              ) : (
                dynamicSections.map((section) => {
                  const iconMap: Record<string, string> = {
                    Company: "ti-building-community",
                    Contact: "ti-user-up",
                    Lead: "ti-chart-arcs",
                    Pipeline: "ti-timeline-event-exclamation",
                    Deal: "ti-medal",
                    Proposal: "ti-file-text",
                    Contract: "ti-file-certificate",
                    Task: "ti-calendar-event",
                    Team: "ti-users",
                    Note: "ti-notes",
                    Document: "ti-file-text",
                    Attachment: "ti-paperclip",
                  };
                  const iconClass = iconMap[section.title] || "ti-api";
                  const isExpanded = expandedSections.includes(section.id);
                  const httpMethods = [
                    ...(section.entityData.get ? ["GET"] : []),
                    ...(section.entityData.single_get ? ["GET /:id"] : []),
                    ...(section.entityData.post ? ["POST"] : []),
                    ...(section.entityData.patch ? ["PATCH"] : []),
                    ...(section.entityData.delete ? ["DELETE"] : []),
                  ];

                  return (
                    <div key={section.id}>
                      <div
                        className={`mobile-menu-item ${selectedSection === section.id && activeTab === "endpoints" ? "active" : ""
                          }`}
                        onClick={() => toggleSection(section.id)}
                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                          <i className={`ti ${iconClass}`}></i>
                          <span>{section.title}</span>
                        </div>
                        <i className={`ti ti-chevron-down section-expand-icon ${isExpanded ? "expanded" : ""}`}></i>
                      </div>
                      {isExpanded && (
                        <div style={{ paddingLeft: "30px" }}>
                          {httpMethods.map((m, index) => (
                            <div
                              key={index}
                              className="mobile-menu-item"
                              onClick={() => { handleMethodClick(section.id, index); setShowMobileMenu(false); }}
                              style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", padding: "8px 12px" }}
                            >
                              <span className={`method-badge-small ${methodColors[m.split(" ")[0]] || "bg-soft-info text-info"}`}>
                                {m.split(" ")[0]}
                              </span>
                              <span style={{ fontSize: "12px" }}>{m}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1050,
              }}
              onClick={() => setShowMobileMenu(false)}
            ></div>
          </>
        )}
      </div>

      <div className="docs-search-section">
        <div className="container">
          <div className="docs-search-box">
            <i className="fa fa-search search-icon"></i>
            <input
              type="text"
              placeholder="Search Cloudee API Documentation"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="clear-search-btn"
                onClick={handleClearSearch}
                aria-label="Clear search"
              >
                <i className="ti ti-x" style={{ fontSize: "18px" }}></i>
              </button>
            )}
            {isSearching && searchResults.length > 0 && (
              <div className="search-results-dropdown">
                {searchResults.map(([groupName, items], groupIndex) => (
                  <div key={groupIndex} className="search-group">
                    <div className="search-group-header">
                      <span className="search-group-title">{groupName}</span>
                      <span className="search-group-count">{items.length}</span>
                    </div>
                    <div>
                      {items.map((result: any, index: number) => (
                        <div
                          key={index}
                          className="search-result-item"
                          onClick={() => handleSearchResultClick(result)}
                        >
                          <div className="search-result-content">
                            <div className="search-result-title">
                              {result.type === "endpoint"
                                ? result.description
                                : result.title}
                            </div>
                            {result.type === "endpoint" && (
                              <div className="search-result-description">
                                {result.endpoint}
                              </div>
                            )}
                          </div>
                          {result.type === "endpoint" && (
                            <span
                              className={`search-result-method ${result.method === "GET"
                                ? "bg-soft-info text-info"
                                : result.method === "POST"
                                  ? "bg-soft-success text-success"
                                  : result.method === "PATCH" ||
                                    result.method === "PUT"
                                    ? "bg-soft-warning text-warning"
                                    : "bg-soft-danger text-danger"
                                }`}
                            >
                              {result.method}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {isSearching &&
              searchQuery.length > 2 &&
              searchResults.length === 0 && (
                <div className="search-results-dropdown">
                  <div className="no-results">
                    <i
                      className="ti ti-search-off"
                      style={{
                        fontSize: "48px",
                        color: "#cbd5e0",
                        display: "block",
                        marginBottom: "12px",
                      }}
                    ></i>
                    <strong style={{ display: "block", marginBottom: "4px" }}>
                      No results found
                    </strong>
                    <span>Try searching with different keywords</span>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      <div className="docs-main-wrapper">
        <div className="docs-sidebar-wrapper">
          <div className="docs-sidebar">
            <h3 className="sidebar-section-title">API Reference</h3>

            <div className="sidebar-subsection">
              <div className="sidebar-subsection-title">Getting Started</div>
              <a
                href="#"
                className={`docs-nav-link ${activeTab === "getting-started" ? "active" : ""
                  }`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("getting-started");
                }}
              >
                <i className="fa-solid fa-rocket me-2" />
                Quick Start
              </a>
              <a
                href="#"
                className={`docs-nav-link ${activeTab === "authentication" ? "active" : ""
                  }`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("authentication");
                }}
              >
                <i className="ti ti-shield-lock me-2"></i>
                Authentication
              </a>
              <a
                href="#"
                className={`docs-nav-link ${activeTab === "scopes" ? "active" : ""
                  }`}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveTab("scopes");
                }}
              >
                <i className="ti ti-lock-access me-2"></i>
                Scopes
              </a>
            </div>

            <div className="sidebar-subsection">
              <div className="sidebar-subsection-title">Endpoints</div>
              {swaggerLoading ? (
                <div style={{ padding: "12px", color: "#718096", fontSize: "13px" }}>
                  <i className="ti ti-loader me-2"></i>Loading...
                </div>
              ) : dynamicSections.length === 0 ? (
                <div style={{ padding: "12px", color: "#a0aec0", fontSize: "13px" }}>
                  No endpoints available
                </div>
              ) : (
                dynamicSections.map((section) => {
                  const iconMap: Record<string, string> = {
                    Company: "ti-building-community",
                    Contact: "ti-user-up",
                    Lead: "ti-chart-arcs",
                    Pipeline: "ti-timeline-event-exclamation",
                    Deal: "ti-medal",
                    Proposal: "ti-file-text",
                    Contract: "ti-file-certificate",
                    Task: "ti-calendar-event",
                    Team: "ti-users",
                    Note: "ti-notes",
                    Document: "ti-file-text",
                    Attachment: "ti-paperclip",
                  };
                  const isExpanded = expandedSections.includes(section.id);
                  const isActive = selectedSection === section.id && activeTab === "endpoints";
                  const httpMethods = [
                    ...(section.entityData.get ? [{ label: "GET", desc: `List all ${section.title}` }] : []),
                    ...(section.entityData.single_get ? [{ label: "GET", desc: `Get ${section.title} by ID` }] : []),
                    ...(section.entityData.post ? [{ label: "POST", desc: `Create ${section.title}` }] : []),
                    ...(section.entityData.patch ? [{ label: "PATCH", desc: `Update ${section.title}` }] : []),
                    ...(section.entityData.delete ? [{ label: "DELETE", desc: `Delete ${section.title}` }] : []),
                  ];

                  return (
                    <div key={section.id}>
                      <div
                        className={`docs-nav-section ${isActive ? "active" : ""}`}
                        onClick={() => toggleSection(section.id)}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <i className={`ti ${iconMap[section.title] || "ti-api"}`}></i>
                          <span>{section.title}</span>
                        </div>
                        <i className={`ti ti-chevron-down section-expand-icon ${isExpanded ? "expanded" : ""}`}></i>
                      </div>
                      <div className={`docs-nav-submenu ${isExpanded ? "expanded" : ""}`}>
                        {httpMethods.map((m, index) => {
                          const methodId = `${section.id}-${index}`;
                          const isMethodActive = selectedMethod === methodId;
                          return (
                            <div
                              key={index}
                              className={`docs-nav-submenu-item ${isMethodActive ? "active" : ""}`}
                              onClick={() => handleMethodClick(section.id, index)}
                            >
                              <span className={`method-badge-small ${methodColors[m.label] || "bg-soft-info text-info"}`}>
                                {m.label}
                              </span>
                              <span style={{ fontSize: "12px", flex: 1, lineHeight: "1.3" }}>
                                {m.desc}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="docs-content-wrapper">
          {activeTab === "getting-started" && (
            <div className="docs-content">
              <h2 className="mb-4">Getting Started</h2>
              <p className="mb-4">
                Welcome to the Cloudee CRM API documentation. This API allows
                you to integrate your applications with our CRM system to manage
                contacts, deals, leads, and more.
              </p>

              <h4 className="mt-4 mb-3">Base URL</h4>
              <CodeBlock
                code={API_BASE_URL || "https://app.cloudee.com/api/v1/"}
                id="base-url"
              />

              <h4 className="mt-4 mb-3">Quick Start</h4>
              <ol className="mb-4">
                <li className="mb-2">
                  Create an API client in your{" "}
                  <Link
                    to={userRole === "apiadmin" ? "/admin-api-integration" : "/integration-settings/api-integration"}
                    style={{ color: "#6b2ee7" }}
                  >
                    API Integration Settings
                  </Link>
                </li>
                <li className="mb-2">
                  Save your API Key, API Secret, and Client ID securely
                </li>
                <li className="mb-2">
                  Use your credentials to authenticate API requests with both
                  headers
                </li>
                <li className="mb-2">
                  Start making API calls to manage your CRM data
                </li>
              </ol>

              <h4 className="mt-4 mb-3">Required Headers</h4>
              <p className="mb-3">
                All API requests must include these headers:
              </p>
              <ul className="mb-4">
                <li>
                  <strong>Authorization:</strong>{" "}
                  <code>Bearer YOUR_API_KEY:YOUR_API_SECRET</code>
                </li>
                <li>
                  <strong>client-id:</strong> <code>YOUR_CLIENT_ID</code>
                </li>
                <li>
                  <strong>Content-Type:</strong> <code>application/json</code>{" "}
                  (for POST/PATCH requests)
                </li>
              </ul>

              <h4 className="mt-4 mb-3">Response Format</h4>
              <p className="mb-3">All responses are returned in JSON format.</p>
              <CodeBlock
                code={`
  // Example Response with pagination
  
  {
  "count": 1,
  "next": "https://next-page.com"
  "previous": null
  "results": [
  {
      "id": 1,
      "name": "John Doe",
      "email": "[EMAIL_ADDRESS]",
      "phone": "1234567890",
      "address": "123 Main St",
      "city": "New York",
      "state": "NY",
      "zip": "12345",
      "country": "USA",
      "createdAt": "2022-01-01T00:00:00Z",
      "updatedAt": "2022-01-01T00:00:00Z"
    }
  ],

}
  
// Example Response without pagination

  [
    {
      "id": 1,
      "name": "John Doe",
      "email": "[EMAIL_ADDRESS]",
      "phone": "1234567890",
      "address": "123 Main St",
      "city": "New York",
      "state": "NY",
      "zip": "12345",
      "country": "USA",
      "createdAt": "2022-01-01T00:00:00Z",
      "updatedAt": "2022-01-01T00:00:00Z"
    }
  ]
`}
                id="response-format"
              />

              <h4 className="mt-4 mb-3">Error Handling</h4>
              <p className="mb-3">The API uses standard HTTP status codes:</p>
              <ul className="mb-4">
                <li>
                  <strong className="text-success">200</strong> - Success
                </li>
                <li>
                  <strong className="text-success">201</strong> - Created
                </li>
                <li>
                  <strong className="text-warning">404</strong> - Not Found
                </li>
                <li>
                  <strong className="text-danger">400</strong> - Bad Request
                </li>
                <li>
                  <strong className="text-danger">401</strong> - Unauthorized
                </li>
                <li>
                  <strong className="text-danger">403</strong> - Forbidden
                </li>
                <li>
                  <strong className="text-danger">500</strong> - Internal Server
                  Error
                </li>
              </ul>

              <CodeBlock
                code={`{
  "error": "Invalid request",
  "message": "The provided parameters are invalid",
  "status_code": 400
}`}
                id="error-response"
              />

              {/* <h4 className="mt-4 mb-3">Rate Limiting</h4>
                <p className="mb-3">
                  API requests are rate-limited to ensure service stability. Current limits:
                </p>
                <ul>
                  <li>1000 requests per hour per API client</li>
                  <li>Rate limit headers are included in all responses</li>
                </ul> */}
            </div>
          )}

          {activeTab === "authentication" && (
            <div className="docs-content">
              <h2 className="mb-4">Authentication</h2>
              <p className="mb-4">
                All API requests require authentication using an API Key, API
                Secret, and Client ID. You can create API credentials in your
                account settings.
              </p>

              <h4 className="mt-4 mb-3">Creating API Credentials</h4>
              <ol className="mb-4">
                <li className="mb-2">
                  Navigate to{" "}
                  <Link
                    to={userRole === "apiadmin" ? "/admin-api-integration" : "/integration-settings/api-integration"}
                    style={{ color: "#6b2ee7" }}
                  >
                    API Integration Settings
                  </Link>
                </li>
                <li className="mb-2">Click "Create API Client"</li>
                <li className="mb-2">
                  Provide a name and description for your client
                </li>
                <li className="mb-2">
                  Select appropriate scopes for your integration
                </li>
                <li className="mb-2">
                  Save your API Key, API Secret, and Client ID securely
                </li>
              </ol>

              <div className="alert alert-warning mb-4">
                <i className="ti ti-alert-circle me-2"></i>
                <strong>Important:</strong> The API Secret is only shown once
                when created. Store it securely!
              </div>

              <h4 className="mt-4 mb-3">Authentication Headers</h4>
              <p className="mb-3">
                Include both the Authorization and client-id headers in each
                request:
              </p>
              <CodeBlock
                code={`Authorization: Bearer YOUR_API_KEY:YOUR_API_SECRET
client-id: YOUR_CLIENT_ID`}
                id="auth-header"
              />

              <div className="alert alert-info mt-3 mb-4">
                <i className="ti ti-info-circle me-2"></i>
                <strong>Note:</strong> The Authorization header uses your API
                Key and API Secret separated by a colon (:), formatted as{" "}
                <code>Bearer YOUR_API_KEY:YOUR_API_SECRET</code>
              </div>

              <h4 className="mt-4 mb-3">Example Request</h4>
              <CodeBlock
                code={`curl -X GET "${API_BASE_URL}/contact/" \\
  -H "Authorization: Bearer YOUR_API_KEY:YOUR_API_SECRET" \\
  -H "client-id: YOUR_CLIENT_ID" \\
  -H "Content-Type: application/json"`}
                id="example-request"
              />

              <h4 className="mt-4 mb-3">Security Best Practices</h4>
              <ul className="mb-4">
                <li>
                  Never expose your API Key, API Secret, or Client ID in
                  client-side code
                </li>
                <li>Use HTTPS for all API requests</li>
                <li>Regenerate your credentials if compromised</li>
                <li>Use scopes to limit API access to only what's needed</li>
                <li>Monitor your API usage regularly</li>
                <li>
                  Store credentials securely using environment variables or
                  secure vaults
                </li>
              </ul>
            </div>
          )}

          {activeTab === "endpoints" && (
            <>
              {swaggerLoading ? (
                <div className="docs-content">
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading API endpoints...</p>
                  </div>
                </div>
              ) : dynamicSections.length === 0 ? (
                <div className="docs-content">
                  <div className="text-center py-5">
                    <i className="ti ti-api-app" style={{ fontSize: "64px", color: "#dee2e6" }}></i>
                    <p className="mt-3 text-muted">No endpoints available.</p>
                  </div>
                </div>
              ) : (
                dynamicSections.map((section) => {
                  const entity = section.entityData;
                  const entityPath = `/${section.entityKey}/`;

                  const renderFieldTable = (fields: SwaggerField[], tableId: string) => (
                    <table className="param-table">
                      <thead>
                        <tr>
                          <th style={{ width: "30%" }}>Name</th>
                          <th style={{ width: "15%" }}>Type</th>
                          <th style={{ width: "15%" }}>Required</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fields.map((f, i) => {
                          const isRequired = f.required || f.name === "tenant_id";
                          return (
                            <tr key={`${tableId}-${i}`}>
                              <td><code>{f.name}</code></td>
                              <td><span className="badge bg-light text-dark">{f.type}</span></td>
                              <td>
                                <span className={isRequired ? "required-badge" : "optional-badge"}>
                                  {isRequired ? "REQUIRED" : "OPTIONAL"}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );

                  const buildListResponse = (fields: SwaggerField[], extra?: any) => {
                    const example = buildFieldsJson(fields, extra);
                    if (entity.pagination_enabled) {
                      return `{
  "count": 1,
  "next": null,
  "previous": null,
  "results": [
${example.split("\n").map((l) => "    " + l).join("\n")}
  ]
}`;
                    }
                    return `[
${example.split("\n").map((l) => "  " + l).join("\n")}
]`;
                  };

                  let methodIndex = 0;

                  return (
                    <div key={section.id} id={section.id} className="docs-content">
                      <h2 className="mb-3">{section.title}</h2>
                      <p className="mb-4">{section.description}</p>

                      {entity.get && entity.get.fields && (() => {
                        const cardId = `${section.id}-method-${methodIndex++}`;
                        const isCollapsed = collapsedCards.includes(cardId);
                        return (
                          <div id={cardId} className="endpoint-card">
                            <div
                              className="endpoint-header"
                              onClick={() => toggleCard(cardId)}
                              style={{ cursor: "pointer", userSelect: "none" }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-3">
                                  <span className="badge bg-soft-info text-info px-3 py-2" style={{ fontSize: "14px", fontWeight: "600" }}>GET</span>
                                  <code style={{ fontSize: "16px", color: "#2d3748" }}>{entityPath}</code>
                                </div>
                                <i className={`ti ti-chevron-down section-expand-icon ${isCollapsed ? "" : "expanded"}`} style={{ fontSize: "16px", color: "#718096" }}></i>
                              </div>
                              <p className="mb-0 mt-2" style={{ color: "#6c757d" }}>List all {section.title}</p>
                            </div>
                            {!isCollapsed && (
                              <div className="endpoint-body">
                                {entity.filter_parameters && entity.filter_parameters.length > 0 && (
                                  <>
                                    <h5 className="mb-3">Filter Parameters</h5>
                                    {renderFieldTable(entity.filter_parameters, `${section.id}-get-filter`)}
                                  </>
                                )}
                                <h5 className="mb-3 mt-4">Example Response</h5>
                                <CodeBlock code={buildListResponse(entity.get.fields, entity.get.extra)} id={`${section.id}-get-resp`} />
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {entity.single_get && entity.single_get.fields && (() => {
                        const cardId = `${section.id}-method-${methodIndex++}`;
                        const isCollapsed = collapsedCards.includes(cardId);
                        return (
                          <div id={cardId} className="endpoint-card">
                            <div
                              className="endpoint-header"
                              onClick={() => toggleCard(cardId)}
                              style={{ cursor: "pointer", userSelect: "none" }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-3">
                                  <span className="badge bg-soft-info text-info px-3 py-2" style={{ fontSize: "14px", fontWeight: "600" }}>GET</span>
                                  <code style={{ fontSize: "16px", color: "#2d3748" }}>{entityPath}:id/</code>
                                </div>
                                <i className={`ti ti-chevron-down section-expand-icon ${isCollapsed ? "" : "expanded"}`} style={{ fontSize: "16px", color: "#718096" }}></i>
                              </div>
                              <p className="mb-0 mt-2" style={{ color: "#6c757d" }}>Get {section.title} by ID</p>
                            </div>
                            {!isCollapsed && (
                              <div className="endpoint-body">
                                <h5 className="mb-3">Example Response</h5>
                                <CodeBlock code={buildFieldsJson(entity.single_get.fields, entity.single_get.extra)} id={`${section.id}-single-get-resp`} />
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {entity.post && entity.post.fields && (() => {
                        const cardId = `${section.id}-method-${methodIndex++}`;
                        const isCollapsed = collapsedCards.includes(cardId);
                        return (
                          <div id={cardId} className="endpoint-card">
                            <div
                              className="endpoint-header"
                              onClick={() => toggleCard(cardId)}
                              style={{ cursor: "pointer", userSelect: "none" }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-3">
                                  <span className="badge bg-soft-success text-success px-3 py-2" style={{ fontSize: "14px", fontWeight: "600" }}>POST</span>
                                  <code style={{ fontSize: "16px", color: "#2d3748" }}>{entityPath}</code>
                                </div>
                                <i className={`ti ti-chevron-down section-expand-icon ${isCollapsed ? "" : "expanded"}`} style={{ fontSize: "16px", color: "#718096" }}></i>
                              </div>
                              <p className="mb-0 mt-2" style={{ color: "#6c757d" }}>Create a new {section.title}</p>
                            </div>
                            {!isCollapsed && (
                              <div className="endpoint-body">
                                <h5 className="mb-3">Request Body Fields</h5>
                                {renderFieldTable(entity.post.fields, `${section.id}-post`)}
                                <h5 className="mb-3 mt-4">Example Request Body</h5>
                                <CodeBlock code={buildFieldsJson(entity.post.fields, entity.post.extra)} id={`${section.id}-post-req`} />
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {entity.patch && entity.patch.fields && (() => {
                        const cardId = `${section.id}-method-${methodIndex++}`;
                        const isCollapsed = collapsedCards.includes(cardId);
                        return (
                          <div id={cardId} className="endpoint-card">
                            <div
                              className="endpoint-header"
                              onClick={() => toggleCard(cardId)}
                              style={{ cursor: "pointer", userSelect: "none" }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-3">
                                  <span className="badge bg-soft-warning text-warning px-3 py-2" style={{ fontSize: "14px", fontWeight: "600" }}>PATCH</span>
                                  <code style={{ fontSize: "16px", color: "#2d3748" }}>{entityPath}:id/</code>
                                </div>
                                <i className={`ti ti-chevron-down section-expand-icon ${isCollapsed ? "" : "expanded"}`} style={{ fontSize: "16px", color: "#718096" }}></i>
                              </div>
                              <p className="mb-0 mt-2" style={{ color: "#6c757d" }}>Partially update {section.title} by ID</p>
                            </div>
                            {!isCollapsed && (
                              <div className="endpoint-body">
                                <h5 className="mb-3">Request Body Fields</h5>
                                {renderFieldTable(entity.patch.fields, `${section.id}-patch`)}
                                <h5 className="mb-3 mt-4">Example Request Body</h5>
                                <CodeBlock code={buildFieldsJson(entity.patch.fields, entity.patch.extra)} id={`${section.id}-patch-req`} />
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {entity.delete && (() => {
                        const cardId = `${section.id}-method-${methodIndex++}`;
                        const isCollapsed = collapsedCards.includes(cardId);
                        return (
                          <div id={cardId} className="endpoint-card">
                            <div
                              className="endpoint-header"
                              onClick={() => toggleCard(cardId)}
                              style={{ cursor: "pointer", userSelect: "none" }}
                            >
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center gap-3">
                                  <span className="badge bg-soft-danger text-danger px-3 py-2" style={{ fontSize: "14px", fontWeight: "600" }}>DELETE</span>
                                  <code style={{ fontSize: "16px", color: "#2d3748" }}>{entityPath}:id/</code>
                                </div>
                                <i className={`ti ti-chevron-down section-expand-icon ${isCollapsed ? "" : "expanded"}`} style={{ fontSize: "16px", color: "#718096" }}></i>
                              </div>
                              <p className="mb-0 mt-2" style={{ color: "#6c757d" }}>Delete {section.title} by ID</p>
                            </div>
                            {!isCollapsed && (
                              <div className="endpoint-body">
                                <p className="text-muted">Returns <code>204 No Content</code> on success.</p>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })
              )}
            </>
          )}

          {activeTab === "scopes" && (
            <div className="docs-content">
              <h2 className="mb-4">API Scopes</h2>
              <p className="mb-4">
                API scopes define the level of access your API client has. You
                can select specific scopes when creating an API client to limit
                access to only the resources you need.
              </p>

              <div className="mb-4">
                <div className="position-relative">
                  <i
                    className="fa fa-search"
                    style={{
                      position: "absolute",
                      left: "15px",
                      top: "35%",
                      transform: "translateY(-50%)",
                      color: "#718096",
                      fontSize: "18px",
                    }}
                  ></i>
                  <input
                    type="text"
                    className="scope-search-box"
                    placeholder="Search scopes by Scope Name or Scope Description..."
                    value={scopeSearchQuery}
                    onChange={(e) => setScopeSearchQuery(e.target.value)}
                    style={{ paddingLeft: "45px" }}
                  />
                  {scopeSearchQuery && (
                    <button
                      style={{
                        position: "absolute",
                        right: "15px",
                        top: "35%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        color: "#718096",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => setScopeSearchQuery("")}
                    >
                      <i className="ti ti-x" style={{ fontSize: "18px" }}></i>
                    </button>
                  )}
                </div>
              </div>

              <div id="scopes-table-section">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading scopes...</p>
                  </div>
                ) : filteredScopes.length === 0 ? (
                  <div className="text-center py-5">
                    <i
                      className="ti ti-search-off"
                      style={{ fontSize: "64px", color: "#dee2e6" }}
                    ></i>
                    <p className="mt-3 text-muted">
                      {scopeSearchQuery
                        ? `No scopes found for "${scopeSearchQuery}"`
                        : "No scopes available"}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div className="text-muted" style={{ fontSize: "14px" }}>
                        Showing {startIndex + 1} to{" "}
                        {Math.min(endIndex, filteredScopes.length)} of{" "}
                        {filteredScopes.length} scopes
                        {scopeSearchQuery &&
                          ` (filtered from ${scopes.length} total)`}
                      </div>
                    </div>
                    <div className="contact-table-wrapper">
                      <table className="table table-striped scope-table contact-table table-bordered">
                        <thead>
                          <tr>
                            <th>Scope Name</th>
                            <th style={{ width: "auto" }}>Scope Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedScopes.map((scope) => (
                            <tr key={scope.api_scope_id}>
                              <td>
                                <div className="d-flex flex-column">
                                  <h6
                                    className="mb-1"
                                    style={{
                                      fontWeight: "600",
                                      fontSize: "14px",
                                      color: "#1f2937",
                                    }}
                                  >
                                    {scope.scope_display_name}
                                  </h6>
                                </div>
                              </td>
                              <td
                                style={{
                                  color: "#4b5563",
                                  fontSize: "14px",
                                  lineHeight: "1.5",
                                }}
                              >
                                {scope.description ||
                                  "No description available"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {totalPages > 1 && (
                      <div className="pagination-container">
                        <nav>
                          <ul className="pagination">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""
                                }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                              >
                                <i className="ti ti-chevron-left"></i>
                                Previous
                              </button>
                            </li>

                            {Array.from(
                              { length: totalPages },
                              (_, i) => i + 1
                            ).map((page) => {
                              if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 &&
                                  page <= currentPage + 1)
                              ) {
                                return (
                                  <li
                                    key={page}
                                    className={`page-item ${currentPage === page ? "active" : ""
                                      }`}
                                  >
                                    <button
                                      className="page-link"
                                      onClick={() => handlePageChange(page)}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              } else if (
                                page === currentPage - 2 ||
                                page === currentPage + 2
                              ) {
                                return (
                                  <li key={page} className="page-item disabled">
                                    <span className="page-link">...</span>
                                  </li>
                                );
                              }
                              return null;
                            })}

                            <li
                              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                                }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                              >
                                Next
                                <i className="ti ti-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="alert alert-info mt-4">
                <i className="ti ti-info-circle me-2"></i>
                <strong>Note:</strong> When creating an API client with custom
                scopes, only select the scopes your application needs. This
                follows the principle of least privilege and enhances security.
              </div>

              <h4 className="mt-5 mb-3">Scope Modes</h4>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="card border-primary">
                    <div className="card-body">
                      <h5 className="card-title">
                        <i className="ti ti-lock-open me-2 text-primary"></i>
                        Global Access
                      </h5>
                      <p className="card-text">
                        Grants full access to all API endpoints and resources.
                        Recommended only for trusted internal integrations.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="card border-success">
                    <div className="card-body">
                      <h5 className="card-title">
                        <i className="ti ti-lock me-2 text-success"></i>
                        Custom Scopes
                      </h5>
                      <p className="card-text">
                        Select specific scopes to limit access to only the
                        resources your application needs. Recommended for
                        third-party integrations.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <hr
        style={{
          borderColor: "#e0e0e0",
          margin: "30px 0",
        }}
      />
      <div
        className="text-center"
        style={{
          color: "#718096",
          background: "#f8f9fa",
          borderTop: "1px solid #e0e0e0",
          padding: "20px 0",
          marginTop: "5px",
        }}
      >
        <p className="mb-0" style={{ fontSize: "14px" }}>
          © {new Date().getFullYear()} Cloudee CRM. All rights reserved.
        </p>
      </div>

      <button
        className={`scroll-to-top ${showScrollTop ? "show" : ""}`}
        onClick={scrollToTop}
        aria-label="Scroll to top"
      >
        <i className="fa-solid fa-arrow-up"></i>
      </button>
    </div>
  );
};

export default ApiDocumentation;
